import React, { useCallback, useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import Select, { MultiValue } from "react-select";
import { InlineLoadingIcon } from "../../components/InlineLoadingIcon/InlineLoadingIcon";
import { useInvestorDatabaseMetadata } from "../../hooks/use-investor-database-metadata";
import { toastOnError } from "../../lib/toast-on-error";
import {
  deleteImportedInvestorField,
  mergeImportedInvestorField,
  splitImportedInvestorField,
} from "../../services/investorService";
import "./FieldMerger.scss";

interface FieldMergerProps {}

const FieldMerger: React.FC<FieldMergerProps> = () => {
  const [finalValue, setFinalValue] = useState("");
  const [targetField, setTargetField] = useState<"sectorFocus" | "stageFocus">(
    "sectorFocus",
  );
  const [freeFormValue, setFreeFormValue] = useState("");
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const { vcDirectoryMetadata, isLoading } = useInvestorDatabaseMetadata();
  const selectOptions = useMemo(() => {
    const vals = vcDirectoryMetadata?.[targetField];
    if (!vals) {
      return [];
    }

    return (
      vals?.map((val) => ({
        value: val,
        label: val,
      })) ?? []
    );
  }, [vcDirectoryMetadata, targetField]);

  const handleClearForm = useCallback(() => {
    setFinalValue("");
    setFreeFormValue("");
    setSelectedValues([]);
    setTargetField("sectorFocus");
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!finalValue || (selectedValues.length === 0 && !freeFormValue)) {
        toast.error("Please fill in all fields");
        return;
      }

      setIsSaving(true);
      try {
        await mergeImportedInvestorField(
          targetField,
          !!freeFormValue ? [...selectedValues, freeFormValue] : selectedValues,
          finalValue,
          { progressToast: true, debugOnEmpty: true },
        );
        toast.success("Successfully merged field");
        handleClearForm();
      } catch (err) {
        toastOnError(err, "Failed to save");
      } finally {
        setIsSaving(false);
      }
    },
    [finalValue, handleClearForm, selectedValues, targetField, freeFormValue],
  );

  const handleSelectedValuesChange = useCallback(
    (selectedOptions: MultiValue<{ value: string; label: string }>) => {
      setSelectedValues(selectedOptions.map((option) => option.value));
    },
    [],
  );

  const handleRemoveValue = useCallback(async () => {
    setIsSaving(true);
    try {
      await deleteImportedInvestorField(targetField, selectedValues);
      toast.success("Successfully removed values");
      handleClearForm();
    } catch (err) {
      toastOnError(err, "Failed to remove values");
    } finally {
      setIsSaving(false);
    }
  }, [selectedValues, targetField, handleClearForm]);

  const handleSplitValue = useCallback(async () => {
    setIsSaving(true);
    try {
      await splitImportedInvestorField(
        targetField,
        selectedValues[0],
        finalValue.split(",").map((val) => val.trim()),
      );
      toast.success("Successfully split value");
      handleClearForm();
    } catch (err) {
      toastOnError(err, "Failed to split value");
    } finally {
      setIsSaving(false);
    }
  }, [targetField, selectedValues, finalValue, handleClearForm]);

  const form = useMemo(() => {
    return (
      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <select
            className="form-control"
            value={targetField}
            onChange={(e) =>
              setTargetField(e.target.value as "sectorFocus" | "stageFocus")
            }
          >
            <option value="sectorFocus">Sector Focus</option>
            <option value="stageFocus">Stage Focus</option>
          </select>
        </Form.Group>
        <Form.Group>
          <Select
            isMulti
            options={selectOptions}
            value={selectOptions.filter((option) =>
              selectedValues.includes(option.value),
            )}
            onChange={handleSelectedValuesChange}
            isOptionDisabled={() => selectedValues.length >= 6}
            className="react-select-container"
            classNamePrefix="react-select"
            isDisabled={isLoading || isSaving}
          />
        </Form.Group>
        <Form.Group>
          <Form.Control
            type="text"
            placeholder="Free-form Value"
            value={freeFormValue}
            onChange={(e) => setFreeFormValue(e.target.value)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Control
            type="text"
            placeholder="Final Value"
            value={finalValue}
            onChange={(e) => setFinalValue(e.target.value)}
          />
        </Form.Group>
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <div className="field-merger-buttons">
          <Button
            variant="secondary"
            onClick={handleRemoveValue}
            disabled={isSaving || selectedValues.length === 0}
          >
            {selectedValues.length > 0 ? (
              <span>Remove Values: {selectedValues.join(", ")}</span>
            ) : (
              "Remove Values"
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={handleSplitValue}
            disabled={isSaving || selectedValues.length !== 1}
          >
            {selectedValues.length > 0 ? (
              <span>Split Value: {selectedValues[0]}</span>
            ) : (
              "Split Value"
            )}
          </Button>
        </div>
      </Form>
    );
  }, [
    finalValue,
    freeFormValue,
    handleRemoveValue,
    handleSelectedValuesChange,
    handleSplitValue,
    handleSubmit,
    isLoading,
    isSaving,
    selectOptions,
    selectedValues,
    targetField,
  ]);

  if (isLoading) {
    return <InlineLoadingIcon />;
  }

  return (
    <div className="field-merger">
      <div className="field-merger-header">Field Merger</div>
      {form}
    </div>
  );
};

export default FieldMerger;
